import { errorDetails, isCloseResponse, isCsrfResponse, isRedirect, isSnapshot, isSuccess } from './responseValidation';
import CheckoutApiError from './CheckoutApiError';
export { default as CheckoutApiError } from './CheckoutApiError';
export class CheckoutStateError extends Error {
    snapshot;
    constructor(snapshot) {
        super('Checkout selection changed');
        this.snapshot = snapshot;
    }
}
export default function createCheckoutApi({ baseUrl, endpoints, csrf, context, errorMessage, transport = (url, init) => fetch(url, init), }) {
    let csrfToken = csrf;
    const endpoint = (action) => endpoints?.[action] ?? `${baseUrl}/${action}`;
    const send = (action, values, token, keepalive = false) => transport(endpoint(action), {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        keepalive,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify(values),
    });
    const request = async (action, values, isValid) => {
        const payload = { ...values, ...context() };
        let response = await send(action, payload, csrfToken);
        const initialError = response.status === 403
            ? await response
                .clone()
                .json()
                .catch(() => null)
            : null;
        if (response.status === 403 && errorDetails(initialError).code === 'invalid_csrf') {
            const tokenResponse = await transport(endpoint('csrf-token'), {
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
            });
            const token = tokenResponse.ok ? await tokenResponse.json().catch(() => null) : null;
            if (!isCsrfResponse(token))
                throw new Error(errorMessage());
            csrfToken = token.csrf;
            response = await send(action, payload, csrfToken);
        }
        if (response.redirected)
            throw new Error(errorMessage());
        const result = await response.json().catch(() => null);
        if (!response.ok) {
            if (action === 'submit' && response.status === 409 && isSnapshot(result)) {
                throw new CheckoutStateError(result);
            }
            const { message, code } = errorDetails(result);
            throw new CheckoutApiError(message ?? errorMessage(), code);
        }
        if (!isValid(result))
            throw new Error(errorMessage());
        return result;
    };
    return {
        start: (variantCode, automatic) => request('start', { variantCode, automatic }, isSnapshot),
        pickup: (session, method, id) => request('pickup', { token: session.token, revision: session.revision, method, id }, isSnapshot),
        code: (session, code) => request('code', { token: session.token, revision: session.revision, code }, isSnapshot),
        submit: (session, policyAgreement, expectedTotal, expectedDeliveryPrice) => request('submit', {
            token: session.token,
            revision: session.revision,
            quote: session.quote,
            policyAgreement,
            expectedTotal,
            expectedDeliveryPrice,
        }, isSuccess),
        authenticate: (session, destination) => request('authenticate', { token: session.token, revision: session.revision, destination }, isRedirect),
        close: token => request('close', { token }, isCloseResponse).then(() => undefined),
        closeOnPageHide: token => {
            send('close', { token, ...context() }, csrfToken, true).catch(() => undefined);
        },
    };
}
